import Hashids from 'hashids';

import { ALPHABET } from './constants';

const hashids = new Hashids("honey doesn't spoil", 8, ALPHABET);

// NOTE: we rely on ALPHABET not having any special symbols
const referenceRegExp = new RegExp(`^[${ALPHABET}]{4}-[${ALPHABET}]{4,}$`);

export const isReference = (ref: string): boolean => referenceRegExp.test(ref);

export const encodeListingRef = (n: number): string => {
  const id = hashids.encode(n);
  return `${id.substring(0, 4)}-${id.substring(4)}`;
};

export const decodeListingRef = (ref: string): number | null => {
  const normalized = ref.replace(/-/g, '').trim();
  if (normalized.length === 0) return null;
  try {
    const decoded = hashids.decode(normalized);
    if (decoded.length === 0) return null;
    const value = Number(decoded[0]);
    return isFinite(value) ? value : null;
  } catch {
    return null;
  }
};

export const decodeReference = (ref: string): number | null =>
  isReference(ref) ? decodeListingRef(ref) : null;

export const decodePortalListingLotReference = (
  originalId: string,
): number | null => {
  const [firstChunk = '', secondChunk = ''] = originalId.split('-');
  return decodeReference(`${firstChunk}-${secondChunk}`);
};

export type PortalListingLotLookup = {
  isUuid: (value: string) => boolean;
  findByReferenceAlias: (decodedReference: number) => Promise<string | null>;
  findById: (lotId: string) => Promise<string | null>;
};

export const resolvePortalListingLotId = async (
  originalId: string,
  lookup: PortalListingLotLookup,
): Promise<string | null> => {
  const decodedReference = decodePortalListingLotReference(originalId);
  if (decodedReference != null) {
    return lookup.findByReferenceAlias(decodedReference);
  }

  return lookup.isUuid(originalId) ? lookup.findById(originalId) : null;
};

type LotWithReferences = {
  lot_reference_aliases: ReadonlyArray<{
    readonly reference_alias_decoded: number;
  }>;
  lot_reference?: string | null;
};

export const computePortalReference = (
  listing: LotWithReferences,
  portalIncrement?: number,
) => {
  const listingRef =
    listing.lot_reference_aliases[0]?.reference_alias_decoded != null
      ? encodeListingRef(
          listing.lot_reference_aliases[0].reference_alias_decoded,
        )
      : listing.lot_reference ?? null;

  return listingRef != null
    ? portalIncrement != null
      ? `${listingRef}-${portalIncrement}`
      : listingRef
    : null;
};
